import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Label from '../Label/Label';
import Loading from '../Loading/Loading';
import TimeAgo from '../TimeAgo/TimeAgo';
import PostComment from '../PostComment/PostComment';
import PinIcon from '../Icons/PinIcon';
import {
  getGiphyImageURL,
  parseComments,
  parseSelftext,
} from './PostDetailHelpers';
import EmptyPostDetail from './EmptyPostDetail';
import RedditVideoPreview from './RedditVideoPreview';
import Image from '../Image/Image';
import { cc } from '../../modules/styles';
import { showMessage } from '../../actions';
import SaveButton from './SaveButton';
import Icon from '../Icons/Icon';
import { openImageGallery, votePost } from '../../actions';

import './PostDetail.css';
import { useDispatch } from 'react-redux';
import { openImageModal } from '../../store/modal/imageModalSlice';

/**
 * Shows the detailed view of a post and its comments
 */

function PostDetail({ post, expandedReplies, reddit }) {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPostId, setCurrentPostId] = useState(null);
  const [imageLoaded, setImageLoaded] = useState(false);

  useEffect(() => {
    if (post && post.id && post.id !== currentPostId) {
      setLoading(true);
      setComments([]);
      setImageLoaded(false);

      const postId = post.id;

      reddit
        .oauthRequest({ uri: `/comments/${postId}/` })
        .then(data => {
          const parsedComments = parseComments(data, post.author);
          setComments(parsedComments);
          setLoading(false);
          setCurrentPostId(postId);
        })
        .catch(err => {
          setLoading(false);
          showMessage(`Error Getting Comments Data: ${err}`);
          console.error('Error Getting Comments Data', err);
        });
    }
  }, [post, currentPostId, reddit]);

  const dispatch = useDispatch();

  const _openImageModal = imageUrl => {
    dispatch(openImageModal(imageUrl));
  };

  if (!post || !post.id) {
    return <EmptyPostDetail />;
  }

  const {
    url,
    isSelf,
    title,
    domain,
    subreddit,
    created_utc,
    num_comments,
    author,
    permalink,
    selftext_html,
    stickied,
    nsfw,
    label,
    saved,
    likes,
    media,
    previewUrl,
    previewImage,
    galleryImages = null,
  } = post;

  const parsedSelftext = parseSelftext(selftext_html);
  const isGallery = galleryImages !== null && galleryImages.length > 1;
  const galleryItemsAmount = isGallery ? galleryImages.length : 0;

  // Links previews detection
  const videoData = getRedditVideoData(media);
  const vredditURL = videoData ? videoData.url : null;
  const youtubeID = !vredditURL ? getYouTubeVideoIDfromURL(url) : null;
  const gfycatID = !youtubeID ? getGfycatVideoIDfromURL(url) : null;
  const imgurVideo = !gfycatID ? getImgurGifVideo(url) : null;
  const imageUrl = !imgurVideo && previewUrl ? previewUrl : checkImageLink(url);
  const imagePreviewUrl = isGallery
    ? galleryImages[0] && galleryImages[0].url
    : imageUrl || (previewImage && previewImage.url);

  const showImagePreview =
    !vredditURL && !youtubeID && !gfycatID && !imgurVideo && imagePreviewUrl;

  const voted = likes !== null;
  const liked = voted && likes;

  function handleClicks(ev) {
    const targetEl = ev.target;
    if (targetEl.tagName !== 'A') return;
    const imageURL =
      getGiphyImageURL(targetEl.href) || checkImageLink(targetEl.href);
    if (!imageURL) return;
    ev.preventDefault();
    _openImageModal(imageURL);
    return false;
  }

  return (
    <div className="PostDetail">
      <div className="PostDetail__header PostDetail__header--main">
        <a
          className="PostDetail__title"
          href={url}
          target="_blank"
          rel="noopener noreferrer"
        >
          {stickied && <PinIcon className="PostDetail__pin-icon" size="1em" />}
          {title}
        </a>

        <div style={{ display: 'flex', marginBottom: '0.25rem' }}>
          <div>
            {/* TODO: turn to text - with aria-hidden 'domain' label */}
            <a
              style={{
                display: 'block',
                color: 'var(--accent-color)',
                textDecoration: 'none',
              }}
              href={url}
              target="_blank"
              rel="noopener noreferrer"
            >
              {domain}
            </a>
            <div style={{ color: 'var(--text-color-2)' }}>by {author}</div>
          </div>

          <div className="PostDetail__buttons">
            <button
              className="PostDetail__vote-button"
              onClick={() => {
                votePost(reddit, {
                  postId: post.id,
                  dir: voted && liked ? 0 : 1,
                });
              }}
              title={voted && liked ? 'Remove upvote' : 'Upvote post'}
            >
              <Icon
                name="triangle"
                size="1.125rem"
                className="PostDetail__vote-icon"
                style={{
                  strokeWidth: 1.5,
                  fill: voted && liked ? 'currentColor' : 'transparent',
                }}
              />
            </button>
            <button
              className="PostDetail__vote-button PostDetail__vote-button--down"
              style={{ position: 'relative', top: -2 }}
              onClick={() => {
                votePost(reddit, {
                  postId: post.id,
                  dir: voted && !liked ? 0 : -1,
                });
              }}
              title={voted && !liked ? 'Remove downvote' : 'Downvote post'}
            >
              <Icon
                name="triangle"
                size="1.125rem"
                rotation={180}
                className="PostDetail__vote-icon"
                style={{
                  strokeWidth: 1.5,
                  fill: voted && !liked ? 'currentColor' : 'transparent',
                }}
              />
            </button>
            <SaveButton
              postId={post.id}
              saved={saved}
              reddit={reddit}
              className="PostDetail__save-button"
            />
            {window.navigator.share && (
              <button
                className="simple-button"
                style={{
                  paddingLeft: 0,
                  paddingRight: 0,
                  marginLeft: '0.25rem',
                }}
                onClick={() => {
                  shareUrl(isSelf ? window.location.href : url, title);
                }}
                title="Share this post"
              >
                <Icon
                  name="share"
                  size="24px"
                  className="PostDetail__share-icon"
                />
              </button>
            )}
          </div>
        </div>

        {(label || nsfw) && (
          <div style={{ display: 'flex' }}>
            {label && (
              <Label text={label} type="basic" className="PostDetail__label" />
            )}
            {nsfw && <Label text="nsfw" type="warning" />}
          </div>
        )}
      </div>

      <div className="PostDetail__header PostDetail__header--meta">
        <div
          style={{
            maxWidth: '33.33%',
            textAlign: 'left',
            paddingLeft: '0.75rem',
          }}
        >
          {subreddit}
        </div>

        <div style={{ textAlign: 'center' }}>
          <TimeAgo timestamp={created_utc} />
        </div>

        <div style={{ textAlign: 'right', paddingRight: '0.75rem' }}>
          <a
            style={{
              color: 'var(--text-color-1)',
              textDecoration: 'none',
            }}
            href={`https://reddit.com${permalink}`}
            target="_blank"
            rel="noopener noreferrer"
            title="Open post in reddit.com"
          >
            {num_comments === 1 ? '1 comment' : `${num_comments} comments`}
          </a>
        </div>
      </div>

      {parsedSelftext && (
        <div
          className="PostDetail__self-text UserContent"
          onClick={handleClicks}
          dangerouslySetInnerHTML={{ __html: parsedSelftext }}
        />
      )}

      {showImagePreview && (
        <>
          <a
            style={{
              display: 'block',
              margin: '0.75rem',
              color: 'var(--text-color-1)',
              textDecoration: 'none',
            }}
            href={imagePreviewUrl}
            onClick={ev => {
              ev.preventDefault();
              if (isGallery) {
                openImageGallery(galleryImages);
              } else {
                _openImageModal(imagePreviewUrl);
              }
            }}
          >
            {/* IMAGE PREVIEW */}
            <img
              src={decodeURIComponent(imagePreviewUrl)}
              className={cc(
                'PostDetail__preview PostDetail__image-preview',
                isGallery ? 'PostDetail__image-preview--gallery' : ''
              )}
              alt={title}
              onLoad={() => {
                setImageLoaded(true);
              }}
              style={{ opacity: imageLoaded ? 1 : 0 }}
            />
            {isGallery && (
              <p className="PostDetail__preview-claimer PostDetail__preview-claimer--gallery">
                Gallery of <b>{galleryItemsAmount} images</b>
              </p>
            )}
          </a>
        </>
      )}

      {youtubeID && (
        <div style={{ margin: '0.75rem' }}>
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            style={{ display: 'block' }}
          >
            <Image
              className="PostDetail__preview"
              url={`//img.youtube.com/vi/${youtubeID}/hqdefault.jpg`}
            />
          </a>
        </div>
      )}

      {gfycatID && (
        <div style={{ margin: '0.75rem' }}>
          <div className="PostDetail__iframe-wrap">
            <iframe
              src={`https://gfycat.com/ifr/${gfycatID}`}
              frameBorder="0"
              scrolling="no"
              allowFullScreen
            />
          </div>
        </div>
      )}

      {(vredditURL || imgurVideo) && (
        <>
          {vredditURL ? (
            <RedditVideoPreview videoData={videoData} originalUrl={url} />
          ) : (
            <video
              src={imgurVideo}
              autoPlay
              playsInline
              muted
              loop
              width={videoData ? videoData.w : undefined}
              height={videoData ? videoData.h : undefined}
              className="PostDetail__preview PostDetail__preview--video"
            />
          )}
        </>
      )}

      {/* TODO: add button to open "comment on post" modal */}

      {comments.length > 0 ? (
        <div
          style={{
            animation: 'reveal-down 100ms linear forwards',
            marginTop: '0.75rem',
          }}
          onClick={handleClicks}
        >
          {comments.map(comment => {
            if (comment.id === 'load-more') {
              return null;
            } else {
              const {
                id,
                body,
                author,
                timeAgo,
                replies,
                byOP,
                vote,
              } = comment;
              return (
                <PostComment
                  key={id}
                  id={id}
                  body={body}
                  author={author}
                  timeAgo={timeAgo}
                  replies={replies}
                  byOP={byOP}
                  postPermalink={permalink}
                  top
                  expandedReplies={expandedReplies}
                  vote={vote}
                  reddit={reddit}
                />
              );
            }
          })}
        </div>
      ) : (
        <>
          {loading ? (
            <div style={{ paddingTop: '1rem', paddingBottom: '1rem' }}>
              <Loading />
            </div>
          ) : (
            <div style={{ textAlign: 'center', margin: '0.75rem' }}>
              No Comments Yet
            </div>
          )}
        </>
      )}
    </div>
  );
}

PostDetail.propTypes = {
  post: PropTypes.object,
  expandedReplies: PropTypes.bool.isRequired,
  reddit: PropTypes.object.isRequired,
};

export default PostDetail;

const checkImageLink = url => {
  let imageURL;
  const matching = url.match(
    /\.(svg|jpe?g|png|gifv?)(?:[?#].*)?$|(?:imgur\.com|livememe\.com|reddituploads\.com)\/([^?#\/.]*)(?:[?#].*)?(?:\/)?$/
  );
  if (!matching) return null;
  if (matching[1]) {
    // normal image link
    if (url.indexOf('.gifv') > 0) url = url.replace('.gifv', '.gif');
    if (url.indexOf('imgur.com') >= 0) url = url.replace(/^htt(p|ps):/, '');
    imageURL = url;
  } else if (matching[2]) {
    if (matching[0].slice(0, 5) === 'imgur') {
      // imgur
      imageURL = `//imgur.com/${matching[2]}.jpg`;
    } else if (matching[0].indexOf('livememe.') >= 0) {
      // livememe
      imageURL = `http://i.lvme.me/${matching[2]}.jpg`;
    } else if (matching[0].indexOf('reddituploads.') >= 0) {
      // reddit media
      imageURL = matching.input;
    } else return null;
  } else return null;

  return imageURL.replace(/&amp;/g, '&');
};

function getImgurGifVideo(imageUrl) {
  if (!/(i.imgur.com).*(\.gifv?$)/.test(imageUrl)) {
    return null;
  }
  const extension = imageUrl.includes('.gifv') ? '.gifv' : '.gif';
  return imageUrl.replace(extension, '.mp4');
}

const getYouTubeVideoIDfromURL = url => {
  const matching = url.match(
    /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/
  );
  if (!matching) return '';
  else {
    if (matching[2].length === 11) return matching[2];
    else return null;
  }
};

const getGfycatVideoIDfromURL = url => {
  const gfycatBaseURL = 'https://gfycat.com/';
  const index = url.indexOf(gfycatBaseURL);
  if (index < 0) return null;
  return url.substring(gfycatBaseURL.length);
};

function shareUrl(url, title) {
  if (navigator.share) {
    navigator.share({
      title: title,
      url: url,
      text: `"${title}" - shared via Satelite.app`,
    });
  }
}

function getRedditVideoData(media) {
  if (!media || !media.reddit_video || !media.reddit_video.fallback_url) {
    return null;
  }

  return {
    url: media.reddit_video.fallback_url,
    dashUrl: media.reddit_video.dash_url,
    w: media.reddit_video.width,
    h: media.reddit_video.height,
  };
}
